import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Clapperboard, Captions, Loader2, Download, Film } from 'lucide-react';
import { toast } from 'sonner';
import { useAdminLang } from '../../hooks/useAdminLang';

const LANGS = [
  ['fr', 'Français'], ['en', 'English'], ['es', 'Español'], ['de', 'Deutsch'],
  ['it', 'Italiano'], ['pt', 'Português'], ['nl', 'Nederlands'], ['ar', 'العربية'],
  ['ru', 'Русский'], ['zh', '中文'], ['ja', '日本語'], ['hi', 'हिन्दी'],
];

/** v50.17 — Admin-only video studio: custom subtitles + free dubbing (<5 min). */
export default function AdminVideoStudio() {
  const { at } = useAdminLang();
  const [mode, setMode] = useState('subs');
  const [file, setFile] = useState(null);
  const [duration, setDuration] = useState(0);
  const [lang, setLang] = useState('');
  const [busy, setBusy] = useState(false);
  const [srt, setSrt] = useState('');
  const [lastBurn, setLastBurn] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [downloading, setDownloading] = useState(null);
  const inputRef = useRef(null);

  const loadJobs = () => axios.get('/api/admin-video-studio/dub/jobs').then(r => setJobs(r.data.jobs || [])).catch(() => {});
  useEffect(() => { if (mode === 'dub') loadJobs(); }, [mode]);

  useEffect(() => {
    if (mode !== 'dub' || !jobs.some(j => j.status === 'processing')) return;
    const iv = setInterval(async () => {
      for (const j of jobs.filter(x => x.status === 'processing')) {
        try { await axios.get(`/api/admin-video-studio/dub/${j.id}/status`); } catch {}
      }
      loadJobs();
    }, 15000);
    return () => clearInterval(iv);
  }, [mode, jobs]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 150 * 1024 * 1024) { toast.error(at('avsTooBig')); return; }
    const url = URL.createObjectURL(f);
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      if (v.duration > 5 * 60) { toast.error(at('avsTooLong')); return; }
      setDuration(v.duration || 0);
      setFile(f);
      setSrt('');
    };
    v.onerror = () => { URL.revokeObjectURL(url); setFile(f); setDuration(0); setSrt(''); };
    v.src = url;
  };

  const fd = () => {
    const d = new FormData();
    d.append('file', file);
    d.append('duration_sec', String(duration || 60));
    return d;
  };

  const generateSubs = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const d = fd();
      d.append('target_lang', lang);
      const r = await axios.post('/api/admin-video-studio/subtitles/generate', d, { timeout: 600000 });
      setSrt(r.data.srt || '');
      toast.success(at('avsOk'));
    } catch (e) { toast.error(e?.response?.data?.detail || at('avsKo')); }
    finally { setBusy(false); }
  };

  const burnSubs = async () => {
    if (!file || !srt.trim()) return;
    setBusy(true);
    try {
      const d = fd();
      d.append('srt_text', srt);
      const r = await axios.post('/api/admin-video-studio/subtitles/burn', d, { responseType: 'blob', timeout: 600000 });
      const url = URL.createObjectURL(r.data);
      const name = `${(file.name || 'video').replace(/\.[^.]+$/, '')}-subtitled.mp4`;
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      setLastBurn(prev => { if (prev) URL.revokeObjectURL(prev.url); return { url, name }; });
    } catch { toast.error(at('avsKo')); }
    finally { setBusy(false); }
  };

  const runDub = async () => {
    if (!file || !lang) { toast.error(at('avsKo')); return; }
    setBusy(true);
    try {
      const d = fd();
      d.append('target_lang', lang);
      await axios.post('/api/admin-video-studio/dub/create', d, { timeout: 600000 });
      toast.success(at('avsOk'));
      setFile(null); setDuration(0);
      if (inputRef.current) inputRef.current.value = '';
      loadJobs();
    } catch (e) { toast.error(e?.response?.data?.detail || at('avsKo')); }
    finally { setBusy(false); }
  };

  const download = async (job) => {
    setDownloading(job.id);
    try {
      const r = await axios.get(`/api/admin-video-studio/dub/${job.id}/download`, { responseType: 'blob', timeout: 600000 });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(job.filename || 'video').replace(/\.[^.]+$/, '')}-dub-${job.target_lang}.mp4`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error(at('avsKo')); }
    finally { setDownloading(null); }
  };

  return (
    <div className="mt-4 pt-4 border-t border-sky-500/20" data-testid="admin-video-studio">
      <p className="text-sm font-bold text-sky-300 flex items-center gap-2 mb-1">
        <Film className="w-4 h-4" /> {at('avsTitle')}
      </p>
      <p className="text-[11px] text-gray-500 mb-3">{at('avsSub')}</p>

      <div className="flex gap-1.5 mb-3">
        <button
          onClick={() => setMode('subs')}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 ${mode === 'subs' ? 'bg-sky-600 text-white' : 'bg-white/10 text-gray-400'}`}
          data-testid="avs-tab-subs"
        >
          <Captions className="w-3.5 h-3.5" /> {at('avsTabSubs')}
        </button>
        <button
          onClick={() => setMode('dub')}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 ${mode === 'dub' ? 'bg-sky-600 text-white' : 'bg-white/10 text-gray-400'}`}
          data-testid="avs-tab-dub"
        >
          <Clapperboard className="w-3.5 h-3.5" /> {at('avsTabDub')}
        </button>
      </div>

      <input ref={inputRef} type="file" accept="video/*" onChange={onFile} className="hidden" data-testid="avs-file-input" />
      <button
        onClick={() => inputRef.current?.click()}
        className="w-full py-2.5 rounded-xl bg-white/10 hover:bg-white/15 border border-white/10 text-sm text-white mb-2"
        data-testid="avs-pick-btn"
      >
        {file ? `${file.name} · ${Math.round(duration)}s` : at('avsPick')}
      </button>

      <div className="mb-2">
        <label className="block text-[11px] text-gray-400 mb-1">{mode === 'subs' ? at('avsLangSubs') : at('avsLangDub')}</label>
        <select
          value={lang}
          onChange={e => setLang(e.target.value)}
          className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-sm text-white"
          data-testid="avs-lang-select"
        >
          {mode === 'subs' && <option value="">{at('avsLangOriginal')}</option>}
          {mode === 'dub' && <option value="">—</option>}
          {LANGS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
        </select>
      </div>

      {mode === 'subs' ? (
        <div className="space-y-2">
          <button
            onClick={generateSubs}
            disabled={!file || busy}
            className="w-full py-2.5 rounded-full bg-gradient-to-r from-sky-500 to-blue-600 hover:brightness-110 text-white font-semibold text-sm disabled:opacity-40 inline-flex items-center justify-center gap-2"
            data-testid="avs-generate-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Captions className="w-4 h-4" />}
            {busy ? at('avsGenerating') : at('avsGenerate')}
          </button>
          {srt && (
            <>
              <p className="text-[10px] text-gray-500">{at('avsEditHint')}</p>
              <textarea
                value={srt}
                onChange={e => setSrt(e.target.value)}
                rows={8}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-[11px] text-white font-mono"
                data-testid="avs-srt-editor"
              />
              <button
                onClick={burnSubs}
                disabled={busy}
                className="w-full py-2.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:brightness-110 text-white font-semibold text-sm disabled:opacity-40 inline-flex items-center justify-center gap-2"
                data-testid="avs-burn-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {busy ? at('avsBurning') : at('avsBurn')}
              </button>
            </>
          )}
          {lastBurn && (
            <a
              href={lastBurn.url}
              download={lastBurn.name}
              className="block text-center text-[11px] text-emerald-300 underline"
              data-testid="avs-redownload"
            >
              {at('avsRedownload')}
            </a>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <button
            onClick={runDub}
            disabled={!file || !lang || busy}
            className="w-full py-2.5 rounded-full bg-gradient-to-r from-sky-500 to-blue-600 hover:brightness-110 text-white font-semibold text-sm disabled:opacity-40 inline-flex items-center justify-center gap-2"
            data-testid="avs-dub-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clapperboard className="w-4 h-4" />}
            {busy ? at('avsDubbing') : at('avsDub')}
          </button>
          {jobs.length > 0 ? (
            <div className="space-y-1.5">
              <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">{at('avsJobs')}</p>
              {jobs.map(j => (
                <div key={j.id} className="flex items-center gap-2 p-2 rounded-xl bg-black/30 border border-white/10" data-testid={`avs-job-${j.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] text-white truncate">{j.filename} → {j.target_lang.toUpperCase()}</p>
                    <p className="text-[10px] text-gray-500">{j.status === 'done' ? at('avsDone') : j.status === 'failed' ? at('avsFailed') : at('avsProcessing')}</p>
                  </div>
                  {j.status === 'processing' && <Loader2 className="w-3.5 h-3.5 animate-spin text-sky-300 shrink-0" />}
                  {j.status === 'done' && (
                    <button
                      onClick={() => download(j)}
                      disabled={downloading === j.id}
                      className="px-2.5 py-1 rounded-full bg-sky-500/20 hover:bg-sky-500/30 text-sky-200 text-[10px] inline-flex items-center gap-1 shrink-0 disabled:opacity-50"
                      data-testid={`avs-download-${j.id}`}
                    >
                      {downloading === j.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                      {at('avsDownload')}
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[10px] text-gray-500 text-center">{at('avsNoJobs')}</p>
          )}
        </div>
      )}
    </div>
  );
}
